import { yupResolver } from "@hookform/resolvers/yup"
import { Divider, Typography } from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import React from "react"
import { useForm } from "react-hook-form"
import { withRouter } from "react-router-dom"
import styled from "styled-components"
import * as Yup from "yup"

import api from "apis"
import { jewlr_t, IS_NA } from "helpers/application"

import {
  FormContainer,
  FormWrapper,
  InputWrapper,
  CancelButton,
  SubmitButton,
  Text,
} from "../components"
import Heading from "../heading"

import ShipAddress from "./ship-address"

const CurrentAddress = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
  line-height: 26px;
`

const validationSchema = Yup.object().shape({
  address1: Yup.string()
    .required(`Please enter your ${jewlr_t("terms.shipping")} address`)
    .min(2, "Minimum length of 2")
    .max(32, "Maximum length of 32"),
  address2: Yup.string().max(
    64,
    "Please enter an optional building, apartment, suite, unit #"
  ),
  city: Yup.string()
    .required(`Please enter a ${jewlr_t("address.city")} name`)
    .max(32, "Maximum length of 32"),
  country: Yup.string().required("Please select your country"),
  postal_code: Yup.string()
    .required("Please enter your postal/zip code")
    .max(7, "Maximum length of 7"),
})

const OrderForm = ({ history, orderData, setCompleted }) => {
  const {
    control,
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    setError,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      address1: "",
      address2: "",
      city: "",
      country: orderData.order.ship_address.country,
      postal_code: "",
    },
    mode: "onBlur",
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = async (values) => {
    try {
      const response = await api.post(
        "/secure/api/v10/order/change_shipping_address",
        {
          order_number: orderData.order.number,
          order_token: orderData.order.token,
          ship_address: values,
        }
      )

      if (response?.data?.action === "payment") {
        history.push(response.data.url)
      } else {
        setCompleted(true)
      }
    } catch (err) {
      setError("root.error", {
        message: err?.errors?.[0] || err?.message || err,
      })
    }
  }

  return (
    <FormContainer>
      <Heading rmaType="change-shipping-address" />
      <FormWrapper>
        <Text fontWeight="500">
          Your Current {jewlr_t("terms.Shipping")} Address:
        </Text>
        <CurrentAddress>{orderData.order.ship_address.address1}</CurrentAddress>
        {orderData.order.ship_address.address2 && (
          <CurrentAddress>
            {orderData.order.ship_address.address2}
          </CurrentAddress>
        )}
        <CurrentAddress>
          {orderData.order.ship_address.city}{" "}
          {orderData.order.ship_address.state}{" "}
          {orderData.order.ship_address.postal_code}
        </CurrentAddress>
        <CurrentAddress>{orderData.order.ship_address.country}</CurrentAddress>
        {IS_NA() && (
          <Text mt={2}>
            Please note: additional sales tax may be charged depending on sales
            tax rate at the updated {jewlr_t("terms.shipping")} address
          </Text>
        )}
      </FormWrapper>
      <Divider bg="grey.20" my={4} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ShipAddress
          control={control}
          country={orderData.order.ship_address.country}
          setValue={setValue}
          subregions={orderData.order.subregions}
          watch={watch}
        />
        {errors?.root?.error && (
          <Typography color="error" mb={1}>
            {errors.root.error.message}
          </Typography>
        )}
        <InputWrapper>
          <SubmitButton
            data-cy="change-shipping-address-submit-button"
            disabled={!isValid}
            loading={isSubmitting}
          >
            Submit
          </SubmitButton>
        </InputWrapper>
        <InputWrapper>
          <CancelButton data-lc="order-form" to="/">
            Cancel and Continue Shopping
          </CancelButton>
        </InputWrapper>
      </form>
    </FormContainer>
  )
}

OrderForm.propTypes = {
  history: PropTypes.object,
  orderData: PropTypes.object,
  setCompleted: PropTypes.func,
}

export default withRouter(OrderForm)
